import {
  createActivity,
  toggleActivityCompletion,
  updateActivity,
  saveTaskReflection,
} from '../supabaseClient/activitiesService';
import { TaskReflectionAgentService } from './index';

/**
 * Execute orchestrator commands on the frontend using existing services.
 *
 * This keeps the frontend as the central place where real side-effects
 * (creating tasks, completing tasks, generating reflections, navigation)
 * actually happen.
 */
export async function executeOrchestratorCommands(commands, options = {}) {
  if (!Array.isArray(commands) || commands.length === 0) return;

  const {
    navigate, // optional: function(view, payload?) -> void
    focusTask, // optional: function(taskId) -> void
  } = options;

  // Execute sequentially to preserve intent order
  // (you can parallelize non-dependent commands later if needed)
  for (const command of commands) {
    if (!command || typeof command !== 'object') continue;

    const { type, payload = {} } = command;

    // eslint-disable-next-line no-console
    console.log('[OrchestratorCommandExecutor] Executing command:', type, payload);

    try {
      switch (type) {
        case 'create_task': {
          const activityData = {
            what: payload.title,
            title: payload.title,
            when: payload.when || null,
            deadline: payload.when || null,
            urgencyLevel: payload.priority || 'medium',
            priority: payload.priority || 'medium',
            projectId: payload.projectId || null,
            courseId: payload.courseId || null,
            description: payload.description || payload.title,
            taskDescription: payload.description || payload.title,
            status: 'todo',
          };
          await createActivity(activityData);
          break;
        }

        case 'complete_task': {
          if (!payload.taskId) break;
          await toggleActivityCompletion(payload.taskId);
          break;
        }

        case 'update_task_notes': {
          if (!payload.taskId) break;
          await updateActivity(payload.taskId, {
            description: payload.notes,
            taskDescription: payload.notes,
          });
          break;
        }

        case 'generate_task_reflection': {
          if (!payload.taskId) break;

          // The caller should provide the full activity object in payload.activity
          const activity = payload.activity;
          if (!activity) break;

          const taskData = {
            userId: activity.user_id,
            taskId: activity.id,
            what: activity.what,
            title: activity.what,
            description: activity.task_description || activity.description || '',
            priority: activity.priority || 'medium',
            urgencyLevel: activity.urgencyLevel,
            learningGoal: activity.learningGoal,
            estimatedMinutes: activity.estimated_minutes,
            timeSpent: activity.time_spent || 0,
            scheduledTime: activity.when,
            startedAt: activity.startedAt,
            completedAt: activity.completedAt || new Date().toISOString(),
            completed: true,
            taskOutcome: 'completed',
            completionPercentage: 100,
            userReflectionResponses: payload.userReflection
              ? { userReflection: payload.userReflection }
              : null,
          };

          const generatedReflection =
            await TaskReflectionAgentService.generateQuickReflection(taskData);

          await saveTaskReflection(activity.id, {
            ...generatedReflection,
            userReflectionResponses: taskData.userReflectionResponses,
          });
          break;
        }

        case 'navigate': {
          if (navigate && payload.view) {
            navigate(payload.view, payload);
          }
          break;
        }

        case 'focus_task': {
          if (focusTask && payload.taskId) {
            focusTask(payload.taskId);
          }
          break;
        }

        default:
          // eslint-disable-next-line no-console
          console.warn('[OrchestratorCommandExecutor] Unknown command type:', type);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[OrchestratorCommandExecutor] Error executing command:', type, err);
    }
  }
}

export default executeOrchestratorCommands;
